import { useEffect, useState } from "react";
import { fetchMentorRequests, fetchHrMentorRequestsDashboard } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { GraduationCap, CheckCircle2, XCircle, Clock, Inbox } from "lucide-react";
import MentorRequestRow from "./MentorRequestRow";

function KpiCard({ label, value, color = "slate", icon: Icon }) {
  const ring = {
    slate: "border-slate-200 text-slate-800",
    amber: "border-amber-300 text-amber-800 bg-amber-50/40",
    emerald: "border-emerald-300 text-emerald-800 bg-emerald-50/40",
    rose: "border-rose-300 text-rose-800 bg-rose-50/40",
    sky: "border-sky-300 text-sky-800 bg-sky-50/40",
  }[color];
  return (
    <div className={`rounded-xl border-2 px-5 py-4 ${ring}`}>
      <div className="flex items-center justify-between">
        <div className="text-[0.65rem] uppercase tracking-wider opacity-80">{label}</div>
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
      </div>
      <div className="font-mono text-3xl font-semibold mt-1.5">{value}</div>
    </div>
  );
}

export default function MentorRequestsPanel() {
  const [dash, setDash] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [d, listResp] = await Promise.all([
        fetchHrMentorRequestsDashboard(),
        fetchMentorRequests(filter === "all" ? {} : { status: filter }),
      ]);
      setDash(d);
      setItems(listResp.items || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleItemUpdate = (updated) => {
    setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
    fetchHrMentorRequestsDashboard().then(setDash).catch(() => {});
  };

  const empty = items.length === 0;
  const pending = dash?.by_status?.pending || 0;
  const approved = dash?.by_status?.approved || 0;
  const rejected = dash?.by_status?.rejected || 0;
  const completed = dash?.by_status?.completed || 0;

  return (
    <div className="space-y-6" data-testid="mentor-requests-panel">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Toplam" value={dash?.total ?? "—"} icon={Inbox} />
        <KpiCard label="Bekleyen" value={pending} color="amber" icon={Clock} />
        <KpiCard label="Onaylı" value={approved} color="emerald" icon={CheckCircle2} />
        <KpiCard label="Reddedilen" value={rejected} color="rose" icon={XCircle} />
        <KpiCard label="Tamamlanan" value={completed} color="sky" icon={GraduationCap } />
      </div>

      {dash?.top_focus_areas?.length > 0 && (
        <div className="card-surface p-5">
          <div className="label-eyebrow mb-3">En Çok Talep Edilen Gelişim Alanları</div>
          <div className="flex flex-wrap gap-2">
            {dash.top_focus_areas.map((f) => (
              <div key={f.name} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-sky-200 bg-sky-50 text-sky-800 text-xs">
                <span>{f.name}</span>
                <span className="font-mono text-[0.7rem] bg-white text-sky-700 px-1.5 py-0.5 rounded">{f.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-white border border-slate-200 mb-4 p-1.5 h-auto">
          <TabsTrigger value="pending" data-testid="req-filter-pending" className="text-sm px-4 py-1.5 data-[state=active]:bg-amber-50 data-[state=active]:text-amber-900 data-[state=active]:shadow-sm">
            Bekleyen ({pending})
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="req-filter-approved" className="text-sm px-4 py-1.5 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-900 data-[state=active]:shadow-sm">
            Onaylı ({approved})
          </TabsTrigger>
          <TabsTrigger value="rejected" data-testid="req-filter-rejected" className="text-sm px-4 py-1.5 data-[state=active]:bg-rose-50 data-[state=active]:text-rose-900 data-[state=active]:shadow-sm">
            Reddedilen ({rejected})
          </TabsTrigger>
          <TabsTrigger value="completed" data-testid="req-filter-completed" className="text-sm px-4 py-1.5 data-[state=active]:bg-sky-50 data-[state=active]:text-sky-900 data-[state=active]:shadow-sm">
            Tamamlanan ({completed})
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="req-filter-all" className="text-sm px-4 py-1.5 data-[state=active]:bg-slate-100 data-[state=active]:text-slate-900 data-[state=active]:shadow-sm">
            Tümü ({dash?.total ?? 0})
          </TabsTrigger>
        </TabsList>
        <TabsContent value={filter} className="space-y-3">
          {loading && (
            <div className="card-surface p-8" data-testid="mentor-requests-loading">
              <div className="space-y-3">
                <div className="h-4 bg-slate-200/60 rounded animate-pulse w-1/3" />
                <div className="h-3 bg-slate-100 rounded animate-pulse w-2/3" />
              </div>
            </div>
          )}
          {!loading && empty && (
            <div className="card-surface p-12 text-center" data-testid="mentor-requests-empty">
              <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <h3 className="font-display text-lg font-semibold text-slate-900">Bu kategoride talep yok</h3>
              <p className="text-sm text-slate-500 mt-1.5">Çalışanlar mentor talebi gönderdiğinde burada listelenecek.</p>
            </div>
          )}
          {!loading && items.map((it) => (
            <MentorRequestRow key={it.id} item={it} onUpdate={handleItemUpdate} perspective="hr" />
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
